import MarkdownIt from "markdown-it";
import type { IssaGeneratedFile } from "./issaInsightsApi.ts";

/* ── Markdown-it instance ────────────────────────────── */

// Answers echo tenant data - product and kiosk names, uploaded files, grid rows -
// so raw HTML in them is escaped, never rendered (html: false). The only HTML
// that passes is what this module writes itself: the placeholders the views swap
// for React controls, the server's pending markers and generated pictures. Each
// is matched by its exact shape (TRUSTED_HTML), so a look-alike with one more
// attribute stays text.
const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

/** How preprocessAiResponse writes a generated picture. */
const IMAGE_ATTRS = 'loading="lazy" decoding="async" style="max-width:100%;height:auto"';
const IMAGE_OPENER = '<button type="button" data-image-open aria-label="View the generated picture full size">';

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const GENERATED_IMAGE =
  String.raw`<img src="data:image/(?:png|jpeg|gif|webp);base64,[A-Za-z0-9+/=]+" alt="Generated picture"(?: width="\d+" height="\d+")? ` +
  `${escapeRegExp(IMAGE_ATTRS)} />`;
const TRUSTED_HTML = [
  String.raw`<!--(?:CSV_EXPORT_\d+|ISSA_ACTION_\d+|ISSA_FILE_\d+|ISSA_(?:IMAGE|TABLE|BUTTON)_PENDING)-->`,
  `${escapeRegExp(IMAGE_OPENER)}${GENERATED_IMAGE}</button>`,
  GENERATED_IMAGE,
  String.raw`<br\s*/?>`,
].join("|");
/** One trusted fragment starting at `lastIndex`. */
const TRUSTED_AT = new RegExp(`(?:${TRUSTED_HTML})`, "y");
/** A line made only of trusted fragments. */
const TRUSTED_LINE = new RegExp(`^(?:(?:${TRUSTED_HTML})[ \\t]*)+$`);

// A line holding only trusted fragments is an HTML block, as it was with
// html: true, so a placeholder on its own line never lands inside a paragraph.
md.block.ruler.before("html_block", "issa_trusted_block", (state, startLine, _endLine, silent) => {
  if (state.sCount[startLine]! - state.blkIndent >= 4) return false;
  const start = state.bMarks[startLine]! + state.tShift[startLine]!;
  if (state.src.charCodeAt(start) !== 0x3c /* < */) return false;
  const line = state.src.slice(start, state.eMarks[startLine]!).trimEnd();
  if (!TRUSTED_LINE.test(line)) return false;
  if (silent) return true;
  const token = state.push("html_block", "", 0);
  token.map = [startLine, startLine + 1];
  token.content = `${line}\n`;
  state.line = startLine + 1;
  return true;
}, { alt: ["paragraph", "reference", "blockquote"] });

// The same fragments inside a paragraph, list item or table cell.
md.inline.ruler.before("html_inline", "issa_trusted_inline", (state, silent) => {
  if (state.src.charCodeAt(state.pos) !== 0x3c /* < */) return false;
  TRUSTED_AT.lastIndex = state.pos;
  const match = TRUSTED_AT.exec(state.src);
  if (!match || state.pos + match[0].length > state.posMax) return false;
  if (!silent) state.push("html_inline", "", 0).content = match[0];
  state.pos += match[0].length;
  return true;
});

// Open links in new tab
const defaultLinkOpen =
  md.renderer.rules.link_open ??
  function (tokens, idx, options, _env, self) {
    return self.renderToken(tokens, idx, options);
  };
md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const token = tokens[idx];
  if (token) {
    token.attrSet("target", "_blank");
    token.attrSet("rel", "noopener noreferrer");
  }
  return defaultLinkOpen(tokens, idx, options, env, self);
};

// Pictures come only from <image> tags, as data URIs. A markdown image that
// points anywhere else shows its alt text: an injected `![](https://host/?q=…)`
// would make the browser send data to that host without a click.
const defaultImage =
  md.renderer.rules.image ??
  function (tokens, idx, options, _env, self) {
    return self.renderToken(tokens, idx, options);
  };
md.renderer.rules.image = function (tokens, idx, options, env, self) {
  const token = tokens[idx];
  if (token && /^data:image\/(?:png|jpeg|gif|webp);/i.test(token.attrGet("src") ?? "")) {
    return defaultImage(tokens, idx, options, env, self);
  }
  return md.utils.escapeHtml(self.renderInlineAsText(token?.children ?? [], options, env));
};

/* ── Helpers ─────────────────────────────────────────── */

/** Detect image MIME type from the first bytes of a base64 string */
function detectImageMime(b64: string): string {
  if (b64.startsWith("iVBOR")) return "image/png";
  if (b64.startsWith("/9j/")) return "image/jpeg";
  if (b64.startsWith("R0lGOD")) return "image/gif";
  if (b64.startsWith("UklGR")) return "image/webp";
  return "image/png";
}

/** An action descriptor extracted from a <button>{json}</button> tag. */
export interface IssaChatActionRaw {
  action: string;
  body: unknown;
}

/** Result of preprocessing raw AI text */
export interface ProcessedResponse {
  /** Text ready for markdown-it (custom tags replaced) */
  displayText: string;
  /** Raw CSV data blocks extracted from <table>…</table> tags */
  csvBlocks: string[];
  /** Action buttons extracted from <button>…</button> tags */
  actionButtons: IssaChatActionRaw[];
  /** Generated images from <image>…</image> tags, as data URIs, in answer
   *  order — action buttons reference them by 1-based position. */
  images: string[];
  /** The files the answer offers, by export id (32 hex digits) in answer
   *  order; `<!--ISSA_FILE_n-->` in the text marks where file n goes. */
  fileIds: string[];
  /** The follow-up the answer suggests in its last <next> tag, as plain text. */
  nextPrompt: string | null;
}

/** The tag the server leaves where an answer offers a file: the export id of
 *  the copy in the user's My files. */
const FILE_TAG = /<file id="([0-9a-f]{32})"><\/file>/gi;
/** Any other file tag - the model's own `<file>name</file>`, a forged id, a
 *  half-written tag. Never shown: the server only links files it delivered. */
const OTHER_FILE_TAG = /<file\b[^>]*>(?:[^<]*<\/file>)?|<\/file>/gi;

const NEXT_BLOCK = /<next>([\s\S]*?)<\/next>/gi;
const NEXT_UNCLOSED = /<next>[\s\S]*$/i;

/** The follow-up an answer suggests - the text of its last `<next>` tag, as
 *  plain text and capped - or null. Offered as a one-tap "Yes" and as Tab
 *  completion in the composer; it is sent exactly as written. */
export function extractNextPrompt(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let last: string | null = null;
  for (const match of raw.matchAll(NEXT_BLOCK)) last = match[1] ?? null;
  if (!last) return null;
  const text = last.replace(/<[^>]*>/g, "").replace(/[*`]/g, "").replace(/\s+/g, " ").trim();
  return text ? text.slice(0, 200) : null;
}

/** The answer without its `<next>` suggestion, for display, copying and parsing.
 *  An unclosed tag (a cut-off answer) goes with everything after it. */
export function stripNextPrompt(raw: string): string {
  return raw.replace(NEXT_BLOCK, "").replace(NEXT_UNCLOSED, "").trimEnd();
}

/** Strip `// …` line comments outside string literals — the model imitates the
 *  documented example JSON, which carries explanatory comments. String-aware so
 *  `"https://…"` values survive. */
function stripJsonLineComments(s: string): string {
  let out = "";
  let inString = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (inString) {
      out += ch;
      if (ch === "\\") { out += s[i + 1] ?? ""; i++; }
      else if (ch === '"') inString = false;
    } else if (ch === '"') {
      inString = true;
      out += ch;
    } else if (ch === "/" && s[i + 1] === "/") {
      while (i < s.length && s[i] !== "\n") i++;
      out += "\n";
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * Pre-process raw AI response text:
 *  - <note>…</note>    → removed (the model's working notes between tool calls)
 *  - <title>…</title>  → markdown heading
 *  - <table>CSV</table> → stripped from display, CSV extracted for export
 *  - <image>BASE64</image> → inline data-URI img
 *  - <image>filename.png</image> → removed (no URL to serve from)
 *  - <a href="https://…">label</a> → markdown link (raw HTML is escaped)
 */
/** Pixel size of a base64 image, read from its header so the <img> can carry
 *  width/height and reserve its box before it decodes (no layout shift). Only
 *  the first 64 KB is decoded - enough for every header, cheap on each render.
 *  Null when the format or header is not recognised. */
export function imageSize(b64: string, mime: string): { width: number; height: number } | null {
  let bytes: Uint8Array;
  try {
    const clean = b64.replace(/=+$/, "");
    const head = clean.slice(0, 87380 - (87380 % 4));
    bytes = Uint8Array.from(atob(head.slice(0, head.length - (head.length % 4))), (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
  // Out-of-range reads answer 0, which the size check below rejects.
  const at = (i: number) => bytes[i] ?? 0;
  const u16be = (i: number) => (at(i) << 8) | at(i + 1);
  const u16le = (i: number) => at(i) | (at(i + 1) << 8);
  const u24le = (i: number) => at(i) | (at(i + 1) << 8) | (at(i + 2) << 16);
  const u32be = (i: number) => ((at(i) << 24) >>> 0) + (at(i + 1) << 16) + (at(i + 2) << 8) + at(i + 3);
  const ok = (w: number, h: number) => (w > 0 && h > 0 ? { width: w, height: h } : null);

  if (mime === "image/png") return ok(u32be(16), u32be(20));
  if (mime === "image/gif") return ok(u16le(6), u16le(8));
  if (mime === "image/webp" && String.fromCharCode(at(12), at(13), at(14), at(15)) === "VP8X") {
    return ok(u24le(24) + 1, u24le(27) + 1);
  }
  if (mime === "image/jpeg") {
    // Walk the segments to the first start-of-frame marker (SOF0-SOF15 minus DHT/JPG/DAC).
    let i = 2;
    while (i + 9 < bytes.length && at(i) === 0xff) {
      const marker = at(i + 1);
      if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
        return ok(u16be(i + 7), u16be(i + 5));
      }
      i += 2 + u16be(i + 2);
    }
  }
  return null;
}

export interface PreprocessOptions {
  /** Wrap each generated picture in a button (`data-image-open`) so it can be
   *  opened full size by keyboard as well as by click. Only for views that
   *  handle the click - the chat answer does, the agent-insight dialog does not. */
  openableImages?: boolean;
}

export function preprocessAiResponse(raw: string, options: PreprocessOptions = {}): ProcessedResponse {
  const csvBlocks: string[] = [];
  const actionButtons: IssaChatActionRaw[] = [];
  const images: string[] = [];
  const fileIds: string[] = [];
  let text = stripNextPrompt(raw);

  // 0. <note>…</note> → removed. InstantAI strips these server-side (they are
  // the model's memory between tool calls, never answer); this is defense in
  // depth for the streamed partial text and for stored responses.
  text = text.replace(/<note>[\s\S]*?<\/note>/gi, "").replace(/<\/?note>/gi, "");

  // 0b. <file id="…"></file> → placeholder that MessagePair swaps for a file
  // card; every other file tag is dropped.
  text = text
    .replace(FILE_TAG, (_m, id: string) => `<!--ISSA_FILE_${fileIds.push(id.toLowerCase()) - 1}-->`)
    .replace(OTHER_FILE_TAG, "");

  // 1. <title>…</title> → ## heading, also when the model wrapped the title
  text = text.replace(/<title>([\s\S]*?)<\/title>/gi, (_m, t: string) => `## ${t.replace(/\s+/g, " ").trim()}`);

  // 1b. <button>{json}</button> → extract action descriptor, leave a placeholder
  // that MessagePair swaps for real action buttons. Malformed blocks are dropped
  // (never render broken JSON to the user).
  text = text.replace(/<button>([\s\S]*?)<\/button>/gi, (_m, jsonText: string) => {
    try {
      const parsed: unknown = JSON.parse(stripJsonLineComments(jsonText.trim()));
      if (
        parsed && typeof parsed === "object" &&
        typeof (parsed as IssaChatActionRaw).action === "string" &&
        (parsed as IssaChatActionRaw).body && typeof (parsed as IssaChatActionRaw).body === "object"
      ) {
        const idx = actionButtons.length;
        actionButtons.push(parsed as IssaChatActionRaw);
        return `<!--ISSA_ACTION_${idx}-->`;
      }
      console.warn("[preprocessAiResponse] <button> block missing action/body — dropped");
    } catch (e) {
      console.warn("[preprocessAiResponse] Unparseable <button> block dropped:", e);
    }
    return "";
  });

  // 2. <table>CSV</table> → extract CSV, leave a placeholder for inline export button
  text = text.replace(/<table>([\s\S]*?)<\/table>/gi, (_m, csv: string) => {
    const trimmed = csv.trim();
    if (trimmed.length > 0) {
      const idx = csvBlocks.length;
      csvBlocks.push(trimmed);
      return `<!--CSV_EXPORT_${idx}-->`;
    }
    return "";
  });

  // 2b. A tag left without its partner (a cut-off answer) is dropped, not shown as text.
  text = text.replace(/<\/?(?:title|table|button)>/gi, "");

  // 3. <image>…</image> → convert base64 to img tag, or remove filename refs
  text = text.replace(
    /<image>([\s\S]*?)<\/image>/gi,
    (_m, content: string) => {
      const trimmed = content.trim();
      // If it looks like base64 (long, starts with valid base64 char, no dots suggesting filename)
      if (trimmed.length >= 100 && !trimmed.includes(".") && /^[A-Za-z0-9+/]/.test(trimmed)) {
        const b64 = trimmed.replace(/[^A-Za-z0-9+/=]/g, "");
        const mime = detectImageMime(b64);
        const dataUri = `data:${mime};base64,${b64}`;
        images.push(dataUri);
        const size = imageSize(b64, mime);
        const dims = size ? ` width="${size.width}" height="${size.height}"` : "";
        const img = `<img src="${dataUri}" alt="Generated picture"${dims} ${IMAGE_ATTRS} />`;
        return options.openableImages ? `${IMAGE_OPENER}${img}</button>` : img;
      }
      // Filename reference — nothing to show
      return "";
    },
  ).replace(/<\/?image>/gi, "");

  // 4. <a href="https://…">label</a> → markdown link. The prompts ask for HTML
  // links when citing documents; raw HTML stays text, so the link is rebuilt as
  // markdown, which markdown-it checks and opens in a new tab.
  text = text.replace(
    /<a\s[^>]*?\bhref\s*=\s*(["'])((?:https?:\/\/|mailto:)[^"'\s<>]+)\1[^>]*>([\s\S]*?)<\/a>/gi,
    (_m, _quote: string, href: string, label: string) => {
      const name = label.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim() || href;
      return `[${name.replace(/[\\[\]]/g, "\\$&")}](<${href}>)`;
    },
  );

  return { displayText: text, csvBlocks, actionButtons, images, fileIds, nextPrompt: extractNextPrompt(raw) };
}

/** For a read-only view of an answer: each file placeholder becomes a line
 *  naming the file, with nothing to download. */
export function nameFilePlaceholders(
  text: string,
  fileIds: readonly string[],
  files?: readonly IssaGeneratedFile[] | null,
): string {
  const names = new Map((files ?? []).map(file => [file.ExportId.toLowerCase(), file.FileName]));
  return text.replace(/<!--ISSA_FILE_(\d+)-->/g, (_m, index: string) => {
    const name = names.get(fileIds[Number(index)] ?? "");
    const label = name ? `File: ${name.replace(/[\\`*_{}[\]()#+\-.!|<>]/g, "\\$&")}` : "A file";
    return `\n\n*${label}*\n\n`;
  });
}

/** Render preprocessed text → HTML with markdown-it. Raw HTML in the text is
 *  escaped; only the fragments preprocessAiResponse writes pass (TRUSTED_HTML). */
export function renderMarkdown(text: string): string {
  return md.render(text);
}

/** Download a CSV string as a file */
export function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** The answer as copied: <note>, <table> and <image> tags stripped, and each
 *  file the answer offers as `[file: name]`. */
export function responseTextForClipboard(raw: string, files?: readonly IssaGeneratedFile[] | null): string {
  const names = new Map((files ?? []).map(file => [file.ExportId.toLowerCase(), file.FileName]));
  return stripNextPrompt(raw)
    .replace(/<note>[\s\S]*?<\/note>/gi, "")
    .replace(/<table>[\s\S]*?<\/table>/gi, "")
    .replace(/<image>[\s\S]*?<\/image>/gi, "")
    .replace(FILE_TAG, (_m, id: string) => {
      const name = names.get(id.toLowerCase());
      return name ? `[file: ${name}]` : "";
    })
    .replace(OTHER_FILE_TAG, "")
    .trim();
}

/** Copy the answer as `responseTextForClipboard` writes it. */
export async function copyResponseToClipboard(raw: string, files?: readonly IssaGeneratedFile[] | null) {
  await navigator.clipboard.writeText(responseTextForClipboard(raw, files));
}

/** "42s", "12m 21s", "1h 05m" - durations for the working timer and answers. */
export function formatElapsedSeconds(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

export function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/* -- Working-status parsing ---------------------------------------- */

/** Markers the server's stream sanitizer leaves where an <image>, <table> or
 *  <button> block sits in the answer being written - complete or still being
 *  typed (see partial_stream.py *_PENDING_MARKER). The chat renders a loading
 *  skeleton at each occurrence: the picture, the table export, the action card. */
export const ISSA_IMAGE_PENDING_MARKER = "<!--ISSA_IMAGE_PENDING-->";
export const ISSA_TABLE_PENDING_MARKER = "<!--ISSA_TABLE_PENDING-->";
export const ISSA_BUTTON_PENDING_MARKER = "<!--ISSA_BUTTON_PENDING-->";
export const ISSA_PENDING_MARKERS: readonly string[] = [
  ISSA_IMAGE_PENDING_MARKER,
  ISSA_TABLE_PENDING_MARKER,
  ISSA_BUTTON_PENDING_MARKER,
];

/** Typewriter helper: if `index` lands inside an occurrence of `marker`,
 *  return the index just past it so the marker is revealed atomically and
 *  never shows half-typed. */
export function advancePastMarker(text: string, index: number, marker: string): number {
  const windowStart = Math.max(0, index - marker.length + 1);
  const markerStart = text.indexOf(marker, windowStart);
  if (markerStart !== -1 && markerStart < index && index < markerStart + marker.length) {
    return markerStart + marker.length;
  }
  return index;
}

/** `advancePastMarker` for several markers: the cut never lands inside any of
 *  them. Markers never nest, so one pass over the list is enough. */
export function advancePastMarkers(text: string, index: number, markers: readonly string[]): number {
  let cut = index;
  for (const marker of markers) cut = advancePastMarker(text, cut, marker);
  return cut;
}

/** Marker InstantAI prefixes onto status messages emitted while an image is
 *  being generated (see status_messages.py IMAGE_STATUS_PREFIX). The UI strips
 *  it and shows a drawing animation instead of the plain typing dots. */
export const ISSA_IMAGE_STATUS_PREFIX = "[image] ";

export interface ParsedIssaStatus {
  text: string;
  kind: "image" | "text";
}

/** Split a raw prompt-status string into display text and its kind. */
export function parseIssaStatus(raw: string | null | undefined): ParsedIssaStatus | null {
  if (!raw) return null;
  if (raw.startsWith(ISSA_IMAGE_STATUS_PREFIX)) {
    const text = raw.slice(ISSA_IMAGE_STATUS_PREFIX.length).trim();
    return { text: text || "Creating the image…", kind: "image" };
  }
  return { text: raw, kind: "text" };
}
